import { RedactionManagerConfig, SDK } from '@commercetools/frontend-sdk';
import { debugLog } from 'helpers/utils/debug';
import { getLocalizationInfo } from 'project.config';
import { ComposableCommerce, ComposableCommerceEvents } from './composable-commerce';


// No-op redaction manager that skips the expensive recursive object traversal.
// The default RedactionHandler walks every key of every API response to mask
// sensitive fields, which blocks the main thread for hundreds of ms on large
// product/cart payloads (see INP trace: handleSuccesfulCall → redact ~688ms).
// Sensitive fields (tokens, passwords) are never present in frontend responses,
// so redaction is unnecessary and only dev-mode logging could expose them.
const noOpRedactionManager = {
  redact<T>(data: T): T {
    return data;
  },
  redactUrl(url: string): string {
    return url;
  },
};

// Add other integration's custom events to the SDK's generic type here,
// by extending ComposableCommerceEvents with their type using an intersection.
// For example, <ComposableCommerceEvents & OtherEvents>.
// You may also wish to add your own custom events.
class CommercetoolsSDK extends SDK<ComposableCommerceEvents> {
  composableCommerce!: ComposableCommerce;
  // Add any other integrations here.

  constructor() {
    super();
    this.composableCommerce = new ComposableCommerce(this);
    // Initialize your other integrations here.

    this.on('errorCaught', (event) => {
      console.error('SDK error: ', event.data);
    });

    // Opt in with `localStorage.DEBUG` rather than shipping it to every visitor.
    this.on('fetchCalled', (event) => {
      debugLog(
        `${event.data.type === 'action' ? event.data.actionName : event.data.method} called: `,
        event.data,
      );
    });

    this.on('fetchSuccessful', (event) => {
      debugLog(
        `${event.data.type === 'action' ? event.data.actionName : event.data.method}  successful: `,
        event.data,
      );
    });


    // Set up any other custom global event handlers here.
    // Ensure types are created and added to the SDK generic type
    // if specific to your project.
  }

  // A simplified, reusable method for configuring the SDK, as for
  // most cases only the locale and currency require input from runtime, or
  // may change on user input.
  defaultConfigure(localeString: string) {
    const { locale, currency } = getLocalizationInfo(localeString);

    this.configure({
      locale,
      currency,
      extensionVersion: process.env.NEXT_PUBLIC_EXT_BUILD_ID ?? 'dev',
      endpoint: process.env.NEXT_PUBLIC_FRONTASTIC_HOST
        ? (process.env.NEXT_PUBLIC_FRONTASTIC_HOST as string).split('/frontastic')[0]
        : '',
      redactionHandlingOverride: noOpRedactionManager as RedactionManagerConfig,
    });
  }
}

// Create a single instance of the sdk.
const sdk = new CommercetoolsSDK();

// Export only the instance to serve as a singleton throughout
// the project.
export { sdk };
